import React, { useEffect, useRef, useState } from "react"
import { SmileSvg, SortbySvg } from "../../../assets/svg"
import avatar from "../../../assets/imgs/game-details/avatar.png"

import TextareaAutosize from "react-textarea-autosize"
import useGlobalStore from "../../../hooks/useGlobalStore"
import { shortenHex } from "../../../utils/string"
import { createComment, deleteInteractComment, getComments, interactComment } from "../../../services/comments"
import { Mention, MentionsInput } from "react-mentions"
import data from "@emoji-mart/data"
import Picker from "@emoji-mart/react"
import useOutsideClick from "../../../hooks/useOutsideClick"
import CommentItem from "../CommentItem"
import { useTranslation } from "next-i18next"
import useHasWindow from "../../../hooks/useHasWindow"
import { useAccount } from "wagmi"

export const Comments = (props) => {
    const hasWindow = useHasWindow()

    const { t: tCommon } = useTranslation("common")
    const { t: tGameDetails } = useTranslation("game-details")

    const { address: account } = useAccount()
    const { user, setIsOpenLoginModal } = useGlobalStore()

    const gameDetails = props.gameDetails

    const { id: gameId } = gameDetails

    const [paginator, setPaginator] = useState({ total: 0 })
    const [comments, setComments] = useState([])

    const isLoggedIn = () => {
        if (!user.id || !account) {
            setIsOpenLoginModal(true)

            return 0
        }

        return user.id
    }

    const [walletSuggestion, setWalletSuggestion] = useState([])
    const addToWalletSuggestion = (comment) => {
        const suggestion = [{ id: shortenHex(comment.customer.walletAddress) }]
        if (comment.childComments) {
            for (let index = 0; index < comment.childComments.length; index++) {
                const walletAddress = shortenHex(comment.childComments[index].customer.walletAddress)

                if (!suggestion.find((x) => x.id === walletAddress)) {
                    suggestion.push({ id: walletAddress })
                }
            }
        }

        setWalletSuggestion([...suggestion])
    }

    const [commentContent, setCommentContent] = useState("")
    const onCommentChange = (e: React.FormEvent<HTMLTextAreaElement>) => {
        setCommentContent(e.currentTarget.value)
    }

    const onCommentKeypress = async (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault()

            await onHandleComment()

            return false
        }
    }

    const [selectedReplyComment, setSelectedReplyComment] = useState<{
        id: string
        childComments: Array<any>
        replyCount: number
    }>()

    const childCommentContentRef = useRef()
    const [childCommentContent, setChildCommentContent] = useState("")

    const [isShowEmoji, setIsShowEmoji] = useState(false)
    const onEmojiSelect = (emoji) => {
        // @ts-ignore
        const selectionStart = childCommentContentRef.current.selectionStart

        let firstValue = childCommentContent.substring(0, selectionStart + 2)
        const count = (firstValue.match(/\@\(\S+\)/gi) || []).length

        firstValue = childCommentContent.substring(0, selectionStart + count * 2)
        const lastValue = childCommentContent.substring(selectionStart + count * 2, childCommentContent.length)

        setChildCommentContent(`${firstValue.trim()} ${emoji.native} ${lastValue.trim()}`)
    }

    const handleClickOutside = () => {
        setIsShowEmoji(false)
    }

    const emojiRef = useOutsideClick(handleClickOutside)

    const [sortField, setSortField] = useState("")
    const fetchComments = async () => {
        const response = await getComments(gameId, user.id, "", sortField, -1, 1, 20)

        setComments(response.data.data.list)
        setPaginator(response.data.data.paginator)
    }

    const fetchChildComments = async (comment) => {
        const response = await getComments(gameId, user.id, comment.id, "", -1, 1, 20)

        comment.childComments = response.data.data.list

        setComments([...comments])
    }

    const onReplyComment = (comment, parentComment) => {
        setSelectedReplyComment(parentComment)
        const defaultText = `@(${shortenHex(comment.customer.walletAddress)}) `

        setChildCommentContent(defaultText)

        setTimeout(() => {
            const textareaRef = childCommentContentRef.current

            if (textareaRef) {
                // @ts-ignore
                textareaRef.focus()

                // @ts-ignore
                textareaRef.setSelectionRange(defaultText.length, defaultText.length)
            }
        }, 100)

        addToWalletSuggestion(parentComment)
    }

    const onHandleComment = async () => {
        if (!commentContent || !isLoggedIn()) {
            return
        }

        const response = await createComment(gameId, commentContent, "")

        if (response.status === 200) {
            setComments([response.data.data, ...comments])
            setCommentContent("")
        }
    }

    const onChildCommentKeypress = async (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault()

            await onHandleChildComment()

            return false
        }
    }

    const onHandleChildComment = async () => {
        if (!childCommentContent || !isLoggedIn() || !selectedReplyComment) {
            return
        }

        const response = await createComment(gameId, childCommentContent, selectedReplyComment.id)

        if (response.status === 200) {
            selectedReplyComment.childComments = selectedReplyComment.childComments || []
            selectedReplyComment.childComments.unshift(response.data.data)
            selectedReplyComment.replyCount = selectedReplyComment.childComments.length

            setSelectedReplyComment(null)
            setChildCommentContent("")

            setComments([...comments])
        }
    }

    const onLike = async (comment: any) => {
        if (!isLoggedIn()) {
            return
        }

        if (comment.customerLiked) {
            const response = await deleteInteractComment(comment.id)
            if (response.status === 200) {
                comment.customerLiked = false
                comment.likeCount = --comment.likeCount

                setComments([...comments])
            }

            return
        }

        const response = await interactComment(comment.id, 1)
        if (response.status === 200) {
            if (comment.customerDisliked) {
                comment.customerDisliked = false
                comment.dislikeCount = --comment.dislikeCount
            }

            comment.customerLiked = true
            comment.likeCount = ++comment.likeCount

            setComments([...comments])
        }
    }

    const onDisLike = async (comment: any) => {
        if (!isLoggedIn()) {
            return
        }

        if (comment.customerDisliked) {
            const response = await deleteInteractComment(comment.id)

            if (response.status === 200) {
                comment.customerDisliked = false
                comment.dislikeCount = --comment.dislikeCount

                setComments([...comments])
            }

            return
        }

        const response = await interactComment(comment.id, 2)
        if (response.status === 200) {
            if (comment.customerLiked) {
                comment.customerLiked = false
                comment.likeCount = --comment.likeCount
            }

            comment.customerDisliked = true
            comment.dislikeCount = ++comment.dislikeCount

            setComments([...comments])
        }
    }

    useEffect(() => {
        fetchComments()
    }, [sortField])

    const defaultStyle = {
        control: {
            backgroundColor: "#05050F",
            fontSize: 14,
            fontWeight: "normal",
            height: "auto"
        },

        "&multiLine": {
            control: {
                fontFamily: "Inter"
            },
            highlighter: {
                minHeight: 29,
                padding: "0.25rem 17px",
                color: "var(--color-accent-4)",
                border: "none",
                outline: "none"
            },
            input: {
                fontFamily: "Inter",
                minHeight: 29,
                color: "var(--color-neutral-1-100)",
                padding: "0.25rem 17px",
                border: "none",
                outline: "none",
                borderRadius: 5
            }
        },

        "&singleLine": {
            display: "inline-block",
            width: 180,

            highlighter: {
                padding: 1,
                border: "2px inset transparent"
            },
            input: {
                padding: 1,
                border: "2px inset"
            }
        },

        suggestions: {
            list: {
                backgroundColor: "white",
                border: "1px solid rgba(0,0,0,0.15)",
                fontSize: 14
            },
            item: {
                padding: "5px 15px",
                borderBottom: "1px solid rgba(0,0,0,0.15)",
                "&focused": {
                    backgroundColor: "#cee4e5"
                }
            }
        }
    }

    return (
        <div className="rounded-[5px] pt-[34px] pl-[10px] pr-[10px] lg:pl-[20px] lg:pr-0">
            <div className="mb-4 flex items-center text-14">
                {paginator.total > 1
                    ? tGameDetails("details.comments", { count: paginator.total })
                    : tGameDetails("details.comment", { count: paginator.total })}
                <div className="group relative ml-8 flex items-center">
                    <SortbySvg width={17} height={12} className={"stroke-black"} />
                    {!sortField && <span className="ml-2 uppercase">{tGameDetails("details.sortBy")}</span>}
                    {sortField && (
                        <span className="ml-2">
                            {sortField === "_id"
                                ? tGameDetails("details.newestFirst")
                                : tGameDetails("details.topComments")}
                        </span>
                    )}

                    <div className="shadow-[0px_4px_4px_rgba(0,0,0,0.25] absolute top-[20px] z-10 hidden h-[72px] w-[112px] rounded-[4px] border border-[#ADADAD] bg-white group-hover:flex">
                        <div className="b-flex-center m-auto flex-col">
                            <div
                                className="mb-2 cursor-pointer hover:text-accent-1"
                                onClick={() => setSortField("replyCount")}
                            >
                                {tGameDetails("details.topComments")}
                            </div>
                            <div className="hover:text-accent-1" onClick={() => setSortField("_id")}>
                                {tGameDetails("details.newestFirst")}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="mb-4">
                <div className={"flex"}>
                    <div className="mr-3 h-[40px] w-[40px] rounded-[50%]">
                        <img src={avatar.src} />
                    </div>
                    <div className="flex-1 child:w-full">
                        {hasWindow && (
                            <TextareaAutosize
                                value={commentContent}
                                onChange={onCommentChange}
                                onKeyPress={onCommentKeypress}
                                className="b-input h-[46px] border border-[#ADADAD] px-5 py-3"
                                placeholder={
                                    user.id
                                        ? tGameDetails("details.commentPlaceholder", {
                                              wallet: shortenHex(account)
                                          })
                                        : tGameDetails("details.pleaseConnect")
                                }
                            ></TextareaAutosize>
                        )}
                    </div>
                </div>
                <div className={"mt-1 flex justify-end"}>
                    <button
                        className="mr-[10px] text-ssm font-bold uppercase hover:text-accent-1"
                        onClick={() => setCommentContent("")}
                    >
                        {tCommon("button.cancel")}
                    </button>
                    <button
                        disabled={!commentContent}
                        className={
                            "b-button h-[19px] w-[43px] text-ssm font-bold uppercase leading-[13px] hover:bg-accent-1 hover:text-[#0C0B1D]"
                        }
                        onClick={onHandleComment}
                    >
                        {tCommon("button.reply")}
                    </button>
                </div>
            </div>

            {comments.map((comment) => {
                return (
                    <div key={comment.id}>
                        <CommentItem
                            comment={comment}
                            parentComment={comment}
                            onLike={onLike}
                            onDisLike={onDisLike}
                            onReplyComment={onReplyComment}
                            fetchChildComments={fetchChildComments}
                        />
                        <div className={"ml-8"}>
                            {comment.childComments &&
                                comment.childComments.map((childComment) => {
                                    return (
                                        <div key={childComment.id}>
                                            <CommentItem
                                                comment={childComment}
                                                parentComment={comment}
                                                onLike={onLike}
                                                onDisLike={onDisLike}
                                                onReplyComment={onReplyComment}
                                                fetchChildComments={fetchChildComments}
                                            />
                                        </div>
                                    )
                                })}
                            {selectedReplyComment && selectedReplyComment.id === comment.id && (
                                <div className="ml-4">
                                    <MentionsInput
                                        value={childCommentContent}
                                        onChange={(e) => setChildCommentContent(e.target.value)}
                                        onKeyPress={onChildCommentKeypress}
                                        style={defaultStyle}
                                        inputRef={childCommentContentRef}
                                    >
                                        <Mention
                                            trigger="@"
                                            markup={"@(__id__)"}
                                            data={walletSuggestion}
                                            style={{
                                                borderRadius: 4,
                                                color: "#35C4F1",
                                                backgroundColor: "#06252F"
                                            }}
                                        />
                                    </MentionsInput>
                                    <div className="mt-1 flex items-center justify-between">
                                        <div className={"relative"} ref={emojiRef}>
                                            <SmileSvg
                                                className={"hover:fill-accent-1"}
                                                onClick={() => setIsShowEmoji((prevState) => !prevState)}
                                            />
                                            {isShowEmoji && (
                                                <div className={"absolute z-10"}>
                                                    <Picker data={data} onEmojiSelect={onEmojiSelect} />
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <button
                                                className="mr-[10px] text-ssm font-bold uppercase text-neutral-1-100 hover:text-accent-1"
                                                onClick={() => setSelectedReplyComment(null)}
                                            >
                                                {tCommon("button.cancel")}
                                            </button>
                                            <button
                                                disabled={!childCommentContent}
                                                className={
                                                    "b-button h-[19px] w-[43px] text-ssm font-bold uppercase leading-[13px] hover:text-neutral-1-100"
                                                }
                                                onClick={onHandleChildComment}
                                            >
                                                {tCommon("button.reply")}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
